/**
 * AI Retention Insight Card
 * Sprint 2: Shows AI-generated personalized retention recommendations
 * for high/critical risk clients.
 */

import { useEffect } from 'react';
import { Sparkles, Phone, MessageCircle, Mail, User, Monitor, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAIRetentionActions } from '@/hooks/use-ai-retention-actions';
import type { ChurnRiskScore } from '@/lib/churn-risk';

const ACTION_ICONS = {
  call: Phone,
  sms: MessageCircle,
  email: Mail,
  in_person: User,
  in_app: Monitor,
} as const;

const PRIORITY_STYLES = {
  urgent: 'border-destructive/30 bg-destructive/5',
  high: 'border-warning/30 bg-warning/5',
  medium: 'border-border',
} as const;

interface AIRetentionInsightProps {
  clientName: string;
  riskScore: ChurnRiskScore | null;
  clientContext?: {
    totalSessions?: number;
    memberSince?: string;
    lastSessionDate?: string;
    packageSessionsLeft?: number;
  };
  className?: string;
}

export function AIRetentionInsight({
  clientName,
  riskScore,
  clientContext,
  className,
}: AIRetentionInsightProps) {
  const { insight, loading, error, generateInsight } = useAIRetentionActions();

  useEffect(() => {
    if (riskScore && (riskScore.level === 'high' || riskScore.level === 'critical')) {
      generateInsight(clientName, riskScore, clientContext);
    }
  }, [clientName, riskScore, clientContext, generateInsight]);

  // Only show for high/critical risk
  if (!riskScore || (riskScore.level !== 'high' && riskScore.level !== 'critical')) {
    return null;
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center gap-3 py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Generating AI recommendations...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !insight || insight.actions.length === 0) {
    return null; // Fall back to template actions silently
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          AI Retention Coach
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">{insight.summary}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {insight.actions.map((action, i) => {
            const Icon = ACTION_ICONS[action.type];
            const priorityStyle = PRIORITY_STYLES[action.priority];

            return (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded-lg border ${priorityStyle}`}
              >
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{action.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {action.type.replace('_', ' ')}
                    </Badge>
                    {action.priority === 'urgent' && (
                      <Badge variant="destructive" className="text-[10px] h-4 px-1.5">
                        urgent
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
